import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Alert } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';
import LicenceUpload from '../components/LicenceUpload';
import { StatusPill } from './StaffRegistrationList';

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day:'2-digit', month:'long', year:'numeric' }) : '');
const fmtAbn  = (a) => (a ? a.replace(/\s/g, '').replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4') : '');
const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-AU', { day:'2-digit', month:'short', hour:'numeric', minute:'2-digit', hour12:true }) : '');
const todayISO = () => new Date(new Date().getTime() + 8*60*60*1000).toISOString().split('T')[0];

const Grid = ({ children }) => (
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:'0 12px' }}>{children}</div>
);
const show = (label, value, mono) => (
  <Field label={label}>
    <div style={{ padding:'9px 12px', background:'var(--bg)', borderRadius:'var(--radius)', border:'1px solid var(--border)', fontSize:'14px', color: value ? 'var(--text)' : 'var(--hint)', minHeight:'38px', ...(mono ? { fontFamily:'JetBrains Mono,monospace', fontSize:'13px' } : {}) }}>
      {value || '—'}
    </div>
  </Field>
);

// Staff Registration → review one submission: details, licence, approve / send back
export default function StaffRegistrationDetail() {
  const { id }   = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner  = user?.role === 'owner';

  const [r,       setR]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice,  setNotice]  = useState(null);
  const [panel,   setPanel]   = useState(null);          // 'approve' | 'reject'
  const [startDate, setStartDate] = useState(todayISO());
  const [note,    setNote]    = useState('');
  const [busy,    setBusy]    = useState(false);

  const load = useCallback(() => {
    api.get(`/staff-registrations/${id}`)
      .then(({ data }) => setR(data.registration))
      .catch(err => setNotice({ type:'error', text: err.response?.data?.message || 'Failed to load this registration' }))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const fetchLicence = (side) => () => api.get(`/staff-registrations/${id}/licence/${side}`, { responseType:'blob' }).then(x => x.data);

  const act = async (fn) => {
    setBusy(true); setNotice(null);
    try { await fn(); } catch (err) { setNotice({ type:'error', text: err.response?.data?.message || 'Something went wrong' }); }
    finally { setBusy(false); }
  };

  const approve = () => act(async () => {
    const { data } = await api.post(`/staff-registrations/${id}/approve`, { startDate });
    setPanel(null); load();
    setNotice({ type:'success', text:`Approved — ${data.employee.staffId} has been added to CS Staff.` });
  });

  const applyUpdate = () => act(async () => {
    await api.post(`/staff-registrations/${id}/apply-update`);
    load();
    setNotice({ type:'success', text:'Updated details have been applied to their CS Staff record.' });
  });

  const reject = () => act(async () => {
    await api.post(`/staff-registrations/${id}/reject`, { note });
    setPanel(null); setNote(''); load();
    setNotice({ type:'success', text:'Sent back to the staff member to fix and resubmit.' });
  });

  const remove = async () => {
    if (!await confirmDelete(`This registration and the uploaded licence files will be permanently removed.${r.status === 'approved' ? ' The CS Staff record is kept.' : ''}`)) return;
    act(async () => { await api.delete(`/staff-registrations/${id}`); navigate('/staff-registrations'); });
  };

  if (loading) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}><i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/></div>;
  if (!r) return <div style={{ padding:'40px', color:'var(--red)', textAlign:'center' }}>{notice?.text || 'Registration not found'}</div>;

  const title = r.fullName || r.email;
  const canReview = r.status === 'pending';

  return (
    <div style={T.page}>
      <PageHeader title={title} onBack={()=>navigate('/staff-registrations')} actions={
        isOwner && (
          <button onClick={remove} disabled={busy} title="Delete" style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
            <i className="ti ti-trash" aria-label="Delete"/>
          </button>
        )
      }/>

      <div style={{ maxWidth:'700px', margin:'0 auto', padding:'20px 16px 60px' }}>
        {notice && <Alert type={notice.type}>{notice.text}</Alert>}

        <Section title="Status" accent>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' }}>
            <StatusPill status={r.status}/>
            {r.submittedAt && <span style={{ fontSize:'12px', color:'var(--muted)' }}>Submitted {fmtDate(r.submittedAt)}</span>}
            {r.status === 'approved' && r.employeeId && (
              <button onClick={()=>navigate(`/staff/${r.employeeId}`)} style={{ ...T.btnSecondary, padding:'6px 12px', fontSize:'12px' }}>
                Open in CS Staff ({r.staffId})
              </button>
            )}
          </div>
          {(r.submission?.reference || r.inviteReference) && (
            <div style={{ marginTop:'12px', padding:'12px 14px', borderRadius:'var(--radius)', border:`1px solid ${r.submission ? (r.submission.matches ? 'rgba(15,110,86,0.25)' : 'rgba(163,45,45,0.3)') : 'var(--border)'}`, background: r.submission ? (r.submission.matches ? 'var(--green-bg)' : 'var(--red-bg)') : 'var(--bg)', fontSize:'13px', lineHeight:1.6 }}>
              <div style={{ fontWeight:'600', color: r.submission ? (r.submission.matches ? 'var(--green)' : 'var(--red)') : 'var(--text)', marginBottom:'4px' }}>
                {r.submission
                  ? (r.submission.matches
                    ? <><i className="ti ti-circle-check" aria-hidden="true"/> Form was filled in with the code emailed to this person</>
                    : <><i className="ti ti-alert-triangle" aria-hidden="true"/> This form was NOT opened with the latest code emailed to this person — check before approving</>)
                  : 'Registration code'}
              </div>
              <div style={{ color:'var(--text)' }}>
                Invite reference <strong style={{ fontFamily:'JetBrains Mono,monospace' }}>{r.submission?.reference || r.inviteReference}</strong>
                {r.submission?.sentTo && <> · emailed to <strong>{r.submission.sentTo}</strong>{r.submission.sentByName ? <> by {r.submission.sentByName}</> : null}{r.submission.sentAt ? <> on {fmtTime(r.submission.sentAt)}</> : null}</>}
              </div>
              <div style={{ color:'var(--muted)', fontSize:'12px' }}>
                {r.lastOpenedAt && <>Last opened {fmtTime(r.lastOpenedAt)}. </>}
                {r.submission?.usedAt ? <>Submitted {fmtTime(r.submission.usedAt)}; the code stopped working then.</> : <>Not submitted yet — the code is still being used.</>}
              </div>
            </div>
          )}
          {r.status === 'approved' && r.updatedAfterApproval && (
            <div style={{ marginTop:'12px', padding:'12px 14px', background:'var(--amber-bg)', border:'1px solid rgba(133,79,11,0.2)', borderRadius:'var(--radius)' }}>
              <div style={{ fontSize:'13px', color:'var(--amber)', marginBottom:'10px', lineHeight:1.5 }}>
                <strong>Updated by the staff member{r.lastUpdateAt ? ` on ${fmtDate(r.lastUpdateAt)}` : ''}.</strong> The details below are their latest. Check them, then apply them to their CS Staff record.
              </div>
              <button onClick={applyUpdate} disabled={busy} style={{ ...T.btnPrimary, opacity:busy?0.6:1 }}>
                <i className="ti ti-refresh" aria-hidden="true"/> {busy ? 'Applying…' : 'Apply to CS Staff record'}
              </button>
            </div>
          )}
          {r.status === 'rejected' && r.rejectionNote && (
            <div style={{ marginTop:'10px', fontSize:'13px', color:'var(--red)' }}>Sent back: {r.rejectionNote}</div>
          )}
          {r.status === 'draft' && <div style={{ marginTop:'10px', fontSize:'13px', color:'var(--muted)' }}>This person has created an account but hasn’t submitted yet.</div>}

          {canReview && !panel && (
            <div style={{ display:'flex', gap:'8px', marginTop:'14px', flexWrap:'wrap' }}>
              <button onClick={()=>setPanel('approve')} style={T.btnPrimary}><i className="ti ti-check" aria-hidden="true"/> Approve</button>
              <button onClick={()=>setPanel('reject')} style={T.btnSecondary}>Send back</button>
            </div>
          )}

          {panel === 'approve' && (
            <div style={{ marginTop:'14px', padding:'14px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
              <div style={{ fontSize:'13px', color:'var(--text)', marginBottom:'10px', lineHeight:1.5 }}>
                This adds {title} to <strong>CS Staff</strong> with the next staff ID. Choose their start date.
              </div>
              <Field label="Start date *">
                <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={T.input}/>
              </Field>
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={approve} disabled={busy || !startDate} style={{ ...T.btnPrimary, opacity:busy?0.6:1 }}>{busy ? 'Approving…' : 'Confirm approval'}</button>
                <button onClick={()=>setPanel(null)} disabled={busy} style={T.btnSecondary}>Cancel</button>
              </div>
            </div>
          )}

          {panel === 'reject' && (
            <div style={{ marginTop:'14px', padding:'14px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)' }}>
              <Field label="What needs fixing? *">
                <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3} maxLength={500} placeholder="e.g. The licence photo is blurry — please upload a clearer one." style={{ ...T.input, resize:'vertical' }}/>
              </Field>
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={reject} disabled={busy || !note.trim()} style={{ ...T.btnPrimary, opacity:busy||!note.trim()?0.6:1 }}>{busy ? 'Sending…' : 'Send back'}</button>
                <button onClick={()=>setPanel(null)} disabled={busy} style={T.btnSecondary}>Cancel</button>
              </div>
            </div>
          )}
        </Section>

        <Section title="Account" accent>{show('Email', r.email)}</Section>

        <Section title="Employee Details" accent>
          <Grid>{show('First name(s)', r.firstNames)}{show('Surname', r.surname)}</Grid>
          {show('Home address', r.homeAddress)}
          <Grid>
            {show('Date of birth', fmtDate(r.dateOfBirth))}
            {show('Nationality', r.nationality)}
            {show('Mobile tel number', r.mobilePhone, true)}
          </Grid>
        </Section>

        <Section title="Emergency Contact" accent>
          <Grid>{show('Name', r.emergencyName)}{show('Contact tel number', r.emergencyPhone, true)}</Grid>
        </Section>

        <Section title="Bank Account Details for Payroll" accent>
          <Grid>
            {show('Account name', r.accountName)}
            {show('BSB number', r.bsb, true)}
            {show('Account number', r.accountNumber, true)}
            {show('Name of bank', r.bankName)}
          </Grid>
        </Section>

        <Section title="ABN" accent>{show('ABN', fmtAbn(r.abn), true)}</Section>

        <Section title="Driver Licence" accent>
          {show('Current driver licence', r.hasDriverLicence ? 'Yes' : 'No')}
          {r.hasDriverLicence && (
            <>
              <Grid>{show('Licence number', r.licenceNumber, true)}{show('Expiry date', fmtDate(r.licenceExpiry))}</Grid>
              <LicenceUpload label="Front" meta={r.licenceFront} fetchBlob={fetchLicence('front')}/>
              <LicenceUpload label="Back"  meta={r.licenceBack}  fetchBlob={fetchLicence('back')}/>
            </>
          )}
        </Section>

        <Section title="Medical Information" accent>
          <Grid>{show('Doctor’s name', r.doctorName)}{show('Doctor’s contact tel number', r.doctorPhone, true)}</Grid>
          {show('Doctor’s address', r.doctorAddress)}
          {show('Allergies', r.allergies)}
          {show('Medications', r.medications)}
        </Section>

        <Section title="Languages" accent>
          {show('Languages other than English', r.speaksOtherLanguage ? 'Yes' : 'No')}
          {r.speaksOtherLanguage && (
            <>
              {show('Languages spoken', r.languages)}
              <Grid>{show('Knowledge', cap(r.languageLevel))}{show('Spoken / Written', cap(r.languageMode))}</Grid>
            </>
          )}
        </Section>

        <Section title="Signed and Dated" accent>
          {r.signature
            ? <img src={r.signature} alt="Signature" style={{ width:'100%', maxWidth:'360px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'#fafafa' }}/>
            : <div style={{ fontSize:'13px', color:'var(--hint)' }}>Not signed</div>}
          {r.signedDate && <div style={{ fontSize:'12px', color:'var(--muted)', marginTop:'6px' }}>Dated {fmtDate(r.signedDate)}</div>}
        </Section>
      </div>
    </div>
  );
}
